/**
 * Agent CRUD + harness listing fetch wrappers · v0.2 (2026-05-14)
 *
 * Backs the `/workspace/$wid/agents` page. Mirrors the harness CLI's
 * `add-agent` / `list-agents` / `remove-agent` flow so SPA users get
 * the same capability without dropping to the terminal.
 *
 * Backend contracts ·
 *   GET  /api/agents              → { agents: AgentRow[] }
 *   PUT  /api/agents/:agent_id    body: AgentUpsertBody → { ok, agent_id, ... }
 *   DELETE /api/agents/:agent_id  → { ok, agent_id }
 *   GET  /api/harnesses           → { harnesses: HarnessRow[] }
 */

export interface AgentRow {
  agent_id: string;
  agent_kind: 'claude_code' | 'codex' | 'api_only' | 'custom';
  display_name: string;
  harness_id: string | null;
  created_at: number;
  updated_at: number | null;
  config: Record<string, unknown> | null;
}

export interface HarnessRow {
  harness_id: string;
  display_name: string;
  enrolled_at: number;
  last_seen_at: number | null;
  last_seen_age_ms?: number | null;
  online?: boolean;
}

export interface AgentUpsertBody {
  agent_kind: AgentRow['agent_kind'];
  display_name: string;
  harness_id: string;
  config: Record<string, unknown>;
}

function trim(url: string): string {
  return url.replace(/\/$/, '');
}

export async function fetchAgents(
  backendUrl: string,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<AgentRow[]> {
  const resp = await fetchImpl(`${trim(backendUrl)}/api/agents`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    throw new Error(`fetchAgents · HTTP ${resp.status} · ${(await resp.text()).slice(0, 200)}`);
  }
  const body = await resp.json() as { agents: AgentRow[] };
  return body.agents;
}

export async function fetchHarnesses(
  backendUrl: string,
  token: string,
  fetchImpl: typeof fetch = fetch,
): Promise<HarnessRow[]> {
  const resp = await fetchImpl(`${trim(backendUrl)}/api/harnesses`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    throw new Error(`fetchHarnesses · HTTP ${resp.status} · ${(await resp.text()).slice(0, 200)}`);
  }
  const body = await resp.json() as { harnesses: HarnessRow[] };
  return body.harnesses;
}

export async function upsertAgent(
  backendUrl: string,
  token: string,
  agentId: string,
  body: AgentUpsertBody,
  fetchImpl: typeof fetch = fetch,
): Promise<{ ok: boolean; agent_id: string }> {
  const resp = await fetchImpl(`${trim(backendUrl)}/api/agents/${encodeURIComponent(agentId)}`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({})) as { error?: string };
    throw new Error(`upsertAgent · HTTP ${resp.status} · ${err.error ?? '(no detail)'}`);
  }
  return resp.json() as Promise<{ ok: boolean; agent_id: string }>;
}

export async function deleteAgent(
  backendUrl: string,
  token: string,
  agentId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<{ ok: boolean; agent_id: string }> {
  const resp = await fetchImpl(`${trim(backendUrl)}/api/agents/${encodeURIComponent(agentId)}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({})) as { error?: string };
    throw new Error(`deleteAgent · HTTP ${resp.status} · ${err.error ?? '(no detail)'}`);
  }
  return resp.json() as Promise<{ ok: boolean; agent_id: string }>;
}

export const HARNESS_ONLINE_TTL_MS = 120_000;

export function isHarnessOnline(
  row: HarnessRow | undefined,
  now: number = Date.now(),
  ttlMs: number = HARNESS_ONLINE_TTL_MS,
): boolean {
  if (!row) return false;
  if (typeof row.online === 'boolean') return row.online;
  if (typeof row.last_seen_at !== 'number' || !Number.isFinite(row.last_seen_at)) return false;
  const age = Math.max(0, now - row.last_seen_at);
  return age <= ttlMs;
}

export function harnessStatusLabel(row: HarnessRow | undefined): string {
  if (!row) return '未登记';
  return isHarnessOnline(row) ? '在线' : '离线';
}

/** Maps an agent_id to a Greek letter avatar (matches prototype Section 16 ①).
 *  α/β/γ/δ for the first four "well-known" ids · falls back to first char.
 *  Returns single uppercase char for ASCII / single Greek glyph. */
export function avatarGlyph(agentId: string): string {
  const known: Record<string, string> = { alpha: 'α', beta: 'β', gamma: 'γ', delta: 'δ', epsilon: 'ε' };
  if (known[agentId.toLowerCase()]) return known[agentId.toLowerCase()];
  return agentId.charAt(0).toUpperCase();
}

/** Reads system_prompt + cli.command + model.name out of the config blob
 *  in a way that tolerates partial fields. Used for the read-only display. */
export function readConfigSummary(config: Record<string, unknown> | null | undefined): {
  systemPrompt: string;
  model: string;
  bin: string;
} {
  const c: any = config ?? {};
  return {
    systemPrompt: typeof c.system_prompt === 'string' ? c.system_prompt : '',
    model: typeof c?.model?.name === 'string' ? c.model.name : '',
    bin: typeof c?.cli?.command === 'string' ? c.cli.command : '',
  };
}
